/**
 * Service worker registration (Phase 16A, design §6.1). Production builds only.
 *
 * A new build installs in the background and waits. The page hears about it through
 * SW_UPDATE_EVENT, and components/offline/AppUpdater applies it with applyServiceWorkerUpdate()
 * at the first safe, quiet moment (never mid-visit, never with unsynced or unsaved work). Users
 * never see or choose the version.
 */

export const SW_UPDATE_EVENT = 'pelora-sw-update';

let waitingWorker: ServiceWorker | null = null;

const announce = (worker: ServiceWorker) => {
  waitingWorker = worker;
  window.dispatchEvent(new Event(SW_UPDATE_EVENT));
};

export function hasServiceWorkerUpdate(): boolean {
  return waitingWorker !== null;
}

/** Activate the waiting build and reload once it controls the page. */
export function applyServiceWorkerUpdate(): void {
  if (!waitingWorker) return;
  let reloaded = false;
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (reloaded) return;
    reloaded = true;
    window.location.reload();
  });
  waitingWorker.postMessage({ type: 'SKIP_WAITING' });
}

export function registerServiceWorker(): void {
  if (process.env.NODE_ENV !== 'production' || typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href);
  if (publicUrl.origin !== window.location.origin) return; // a CDN-hosted PUBLIC_URL can't serve the worker

  window.addEventListener('load', () => {
    navigator.serviceWorker.register(`${process.env.PUBLIC_URL}/service-worker.js`).then((registration) => {
      if (registration.waiting && navigator.serviceWorker.controller) announce(registration.waiting);
      registration.addEventListener('updatefound', () => {
        const installing = registration.installing;
        if (!installing) return;
        installing.addEventListener('statechange', () => {
          // "installed" with an existing controller = an update (the first install needs no prompt)
          if (installing.state === 'installed' && navigator.serviceWorker.controller) announce(installing);
        });
      });
      // Installed apps can stay open for days: look for a new build every hour
      setInterval(() => { registration.update().catch(() => undefined); }, 60 * 60 * 1000);
    }).catch(() => {
      // Registration failed (private mode, blocked): the app works online without offline support
    });
  });
}
